import { useEffect, useRef, useState, type RefObject } from "react";

import type { FileWithPath } from "../../hooks/useS3Upload";
import {
  containsNativeFiles,
  extractDropEntries,
} from "../../utils/dataTransferEntries";

interface FileDropTargetOptions {
  /** false の間はリスナーを張らず、isDragging は常に false */
  enabled: boolean;
  /** false なら先頭 1 件だけ渡す */
  multiple: boolean;
  onFiles: (files: File[]) => void;
  /** 指定するとフォルダ構造（相対パス）を保ったままこちらへ渡す */
  onFilesWithPath?: (entries: FileWithPath[]) => void;
}

interface FileDropTarget {
  /** ドロップを受ける要素に付ける ref */
  ref: RefObject<HTMLDivElement | null>;
  /** 自分の上 / ページ上のどちらかでファイルがドラッグ中か */
  isDragging: boolean;
}

/**
 * コメント入力欄をファイルのドロップ先にするためのフック。
 *
 * 検知は 2 層に分かれる:
 * - 対象要素: 自分の上に来たドラッグを引き取り、ドロップも自分で処理する。
 *   受け口を addEventListener にしているのは、React の合成イベントがルートまで
 *   上がってから配送される都合で、合成イベント側で stopPropagation しても
 *   入力欄を内側に含む DropZoneOverlay（Preview）のネイティブリスナーを
 *   止められず二重処理になるため。
 * - ページ全体: DropZoneOverlay は表示中 pointer-events: auto で全面を覆うため、
 *   入力欄の上へ運んでもドロップはそちら（= 同じ添付リスト）が受けることが多い。
 *   それでも「添付先はこの入力欄」と分かるよう、window でドラッグを拾って
 *   ハイライトだけは出す。
 *
 * どちらの層も dragenter / dragleave を数える。dragenter は子要素へ移るたびに
 * 発火し、その直後に元要素の dragleave が来るため、単純な真偽値では消えてしまう。
 */
export const useFileDropTarget = ({
  enabled,
  multiple,
  onFiles,
  onFilesWithPath,
}: FileDropTargetOptions): FileDropTarget => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverTarget, setIsOverTarget] = useState(false);
  const [isOverPage, setIsOverPage] = useState(false);

  // ドロップの受け渡し先。effect を貼り直してドラッグ状態が飛ぶのを避けるため
  // 依存配列には入れず、ref 経由で常に最新を読む
  const handlersRef = useRef({ multiple, onFiles, onFilesWithPath });
  useEffect(() => {
    handlersRef.current = { multiple, onFiles, onFilesWithPath };
  });

  // ===== ページ全体のドラッグ検知（ハイライト用） =====
  // 対象要素の上では下の effect が stopPropagation するため window には届かない。
  // その区間は isOverTarget が受け持つので、合算すれば途切れない。
  useEffect(() => {
    if (!enabled) return;

    let depth = 0;
    const handleEnter = (event: DragEvent) => {
      if (!containsNativeFiles(event)) return;
      depth += 1;
      setIsOverPage(true);
    };
    const handleLeave = () => {
      if (depth === 0) return;
      depth -= 1;
      if (depth === 0) setIsOverPage(false);
    };
    const handleEnd = () => {
      depth = 0;
      setIsOverPage(false);
    };

    window.addEventListener("dragenter", handleEnter);
    window.addEventListener("dragleave", handleLeave);
    window.addEventListener("drop", handleEnd);
    window.addEventListener("dragend", handleEnd);

    return () => {
      window.removeEventListener("dragenter", handleEnter);
      window.removeEventListener("dragleave", handleLeave);
      window.removeEventListener("drop", handleEnd);
      window.removeEventListener("dragend", handleEnd);
      setIsOverPage(false);
    };
  }, [enabled]);

  // ===== 対象要素のドロップ受け口 =====
  useEffect(() => {
    const target = ref.current;
    if (!target || !enabled) return;

    let depth = 0;

    const handleDragEnter = (event: DragEvent) => {
      if (!containsNativeFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      depth += 1;
      setIsOverTarget(true);
    };

    // 入場を数えているドラッグだけを引き取る（テキストの D&D 等には触らない）
    const handleDragLeave = (event: DragEvent) => {
      if (depth === 0) return;
      event.stopPropagation();
      depth -= 1;
      if (depth === 0) setIsOverTarget(false);
    };

    const handleDragOver = (event: DragEvent) => {
      const { dataTransfer } = event;
      if (!dataTransfer || !containsNativeFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dataTransfer.dropEffect = "copy";
    };

    const handleDrop = (event: DragEvent) => {
      const { dataTransfer } = event;
      if (!dataTransfer || !containsNativeFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      depth = 0;
      setIsOverTarget(false);

      // dataTransfer は await を挟むと無効化されるため、走査はこの場で開始する
      void extractDropEntries({ dataTransfer }).then((entries) => {
        if (entries.length === 0) return;
        const { multiple, onFiles, onFilesWithPath } = handlersRef.current;
        const accepted = multiple ? entries : entries.slice(0, 1);
        if (onFilesWithPath) {
          onFilesWithPath(accepted);
        } else {
          onFiles(accepted.map((entry) => entry.file));
        }
      });
    };

    target.addEventListener("dragenter", handleDragEnter);
    target.addEventListener("dragleave", handleDragLeave);
    target.addEventListener("dragover", handleDragOver);
    target.addEventListener("drop", handleDrop);

    return () => {
      target.removeEventListener("dragenter", handleDragEnter);
      target.removeEventListener("dragleave", handleDragLeave);
      target.removeEventListener("dragover", handleDragOver);
      target.removeEventListener("drop", handleDrop);
      setIsOverTarget(false);
    };
  }, [enabled]);

  return { ref, isDragging: isOverTarget || isOverPage };
};
